import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, TrendingUp, Users, AlertCircle,
  CheckCircle, MapPin, Filter, Activity, Zap, Banknote,
  Clock, Wifi, WifiOff, AlertTriangle, RefreshCw
} from 'lucide-react';

export const AnalyticsTab = ({
  overview, merchants, customers, errors, integrations, breakdown,
  filters, setFilters, applyFilters, loading
}) => {
  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#1B0C34] to-[#2D1854] text-white">
          <CardHeader>
            <CardDescription className="text-white/70">Total Transactions</CardDescription>
            <CardTitle className="text-3xl">{overview?.totals?.transaction_count || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart className="w-8 h-8 opacity-20" />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#145539] to-[#1e7049] text-white">
          <CardHeader>
            <CardDescription className="text-white/70">Total Volume (ZAR)</CardDescription>
            <CardTitle className="text-3xl">R{overview?.totals?.total_volume?.toFixed(2) || '0.00'}</CardTitle>
          </CardHeader>
          <CardContent>
            <Banknote className="w-8 h-8 opacity-20" />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#F2B10C] to-[#d99a0a] text-[#1B0C34]">
          <CardHeader>
            <CardDescription className="text-[#1B0C34]/70">Merchants</CardDescription>
            <CardTitle className="text-3xl">{merchants?.total_merchants || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Users className="w-8 h-8 opacity-20" />
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#145539] to-[#1e7049] text-white">
          <CardHeader>
            <CardDescription className="text-white/70">Customers</CardDescription>
            <CardTitle className="text-3xl">{customers?.total_customers || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <Users className="w-8 h-8 opacity-20" />
          </CardContent>
        </Card>
      </div>

      {/* Success Rate & Integration Health */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="text-green-600" size={20} />
              Success Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Successful Transactions</p>
              <p className="text-2xl font-bold">{errors?.success_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-xl font-semibold text-green-600">{errors?.success_percentage?.toFixed(1) || 0}%</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="text-blue-600" size={20} />
              Integration Health
            </CardTitle>
            <CardDescription>Real-time integration status and transaction metrics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {integrations?.providers && integrations.providers.length > 0 ? (
              integrations.providers.map((provider) => {
                const isHealthy = provider.real_count > 0;

                return (
                  <div key={provider.name} className="border rounded-lg p-3 bg-gradient-to-r from-gray-50 to-white hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isHealthy ? (
                            <Wifi className="text-green-600" size={16} />
                          ) : (
                            <WifiOff className="text-gray-400" size={16} />
                          )}
                          <span className="font-semibold text-sm">{provider.name}</span>
                          <Badge variant={isHealthy ? "default" : "secondary"} className="text-xs">
                            {isHealthy ? 'Active' : 'Idle'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 gap-2 text-xs">
                          <div>
                            <p className="text-gray-600">Transactions</p>
                            <p className="font-semibold text-green-700">{provider.real_count || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Activity className="mx-auto mb-2 opacity-50" size={32} />
                <p className="text-sm">No integration data available yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Transaction Types */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Types (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {overview?.by_type && typeof overview.by_type === 'object' && Object.keys(overview.by_type).length > 0
              ? Object.entries(overview.by_type).map(([type, data]) => (
                  <div key={type} className="bg-gradient-to-br from-[#F5EDE4] to-[#EFE4D6] p-4 rounded-lg">
                    <p className="text-sm font-semibold text-[#1B0C34]">{type}</p>
                    <p className="text-lg font-bold text-[#145539]">{data.count || 0}</p>
                    <p className="text-xs text-gray-600">R{data.volume?.toFixed(2) || '0.00'}</p>
                  </div>
                ))
              : <p className="col-span-full text-gray-500 text-center py-4">No transaction data available</p>
            }
          </div>
        </CardContent>
      </Card>

      {/* Merchants by Province */}
      <Card>
        <CardHeader>
          <CardTitle>Merchants by Province</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {Array.isArray(merchants?.by_province) && merchants.by_province.length > 0
              ? merchants.by_province.map((item) => (
                  <div key={item.province} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-[#145539]" />
                      <span className="font-medium text-sm">{item.province}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{item.merchant_count} merchants</p>
                      <p className="text-xs text-gray-600">R{item.total_balance?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                ))
              : <p className="text-gray-500 text-center py-4">No merchant data available</p>
            }
          </div>
        </CardContent>
      </Card>

      {/* Granular Analysis Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Detailed Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium">Transaction Type</label>
              <Select value={filters.transaction_type} onValueChange={(value) => setFilters({...filters, transaction_type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="card_payment">Card Payment</SelectItem>
                  <SelectItem value="wallet_transfer">Wallet Transfer</SelectItem>
                  <SelectItem value="money_voucher">Money Voucher</SelectItem>
                  <SelectItem value="airtime_data">Airtime/Data</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Province</label>
              <Input
                placeholder="Province"
                value={filters.province}
                onChange={(e) => setFilters({...filters, province: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              />
            </div>
          </div>

          <Button onClick={applyFilters} className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]">
            Apply Filters
          </Button>
        </CardContent>
      </Card>

      {/* Filtered Results */}
      {breakdown && (
        <Card>
          <CardHeader>
            <CardTitle>Filtered Analysis Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-[#F5EDE4] to-[#EFE4D6] p-4 rounded-lg">
                <p className="text-sm text-gray-600">Transaction Count</p>
                <p className="text-2xl font-bold text-[#1B0C34]">{breakdown.count || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-[#F5EDE4] to-[#EFE4D6] p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Volume</p>
                <p className="text-2xl font-bold text-[#145539]">R{breakdown.total_volume?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="bg-gradient-to-br from-[#F5EDE4] to-[#EFE4D6] p-4 rounded-lg">
                <p className="text-sm text-gray-600">Average Transaction</p>
                <p className="text-2xl font-bold text-[#145539]">R{breakdown.average_amount?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F5EDE4]">
                  <tr>
                    <th className="px-4 py-2 text-left">Type</th>
                    <th className="px-4 py-2 text-left">Time</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-right">Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.transactions?.slice(0, 10).map((tx, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{tx.type}</td>
                      <td className="px-4 py-2 text-xs text-gray-600">{new Date(tx.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right font-semibold">R{tx.amount?.toFixed(2) || '0.00'}</td>
                      <td className="px-4 py-2 text-right text-orange-600">R{tx.fee?.toFixed(2) || '0.00'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
